import { useTranslation } from 'next-i18next'

import { ButtonPrimary } from '~/components/Buttons'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGImage from '~/components/Image/SVGImage'
import RoleCard from '~/classroom/components/RoleCard'
import SchoolOutlineButton from '~/classroom/components/SchoolOutlineButton'
import ROUTES from '~/shared/constants/routes.const'

/** The artwork bleeds off the right edge, so it is painted at its full `w-302` rather than the gap. */
const ORB_DESKTOP_MEDIA_QUERY = [{ screen: '(min-width: 1024px)', width: '1208px' }]

/** Below `lg` the same artwork is a small mark behind the header instead. */
const ORB_MOBILE_MEDIA_QUERY = [{ screen: '(max-width: 1023px)', width: '229px' }]

/**
 * Role selection. The school edition has no marketing site to land on, so this is served at `/` —
 * `pages/index.tsx` picks between this and the b2c landing. The only dark screen in the b2s set.
 */
export default function RoleSelectionPage(): React.ReactElement {
  const { t } = useTranslation(['classroom', 'imageAlt'])

  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-purple-dark" data-test-id="role-selection">
      {/* Soft glows, phone only — the artwork alone leaves the corners flat. */}
      <div
        aria-hidden
        className="absolute -left-12 -top-12 w-44 h-44 rounded-full bg-school-kids-glow-violet lg:hidden"
      />
      <div
        aria-hidden
        className="absolute left-60 top-36 w-40 h-40 rounded-full bg-school-kids-glow-purple lg:hidden"
      />

      {/* Decoration, placed differently per breakpoint. Each is lazy, so only the visible one loads. */}
      {/* Width on the `<picture>`, not the `<img>`: preflight's `max-width: 100%` clamps it otherwise. */}
      <ResponsiveImage
        aria-hidden
        className="absolute top-4 right-2 w-57 -rotate-40 lg:hidden"
        height={202}
        imageClassName="w-full opacity-15 pointer-events-none select-none"
        mediaQuery={ORB_MOBILE_MEDIA_QUERY}
        srcKey="landing/mind-ai-v2.png"
        width={229}
      />
      <ResponsiveImage
        aria-hidden
        className="absolute -top-30 -right-185 w-302 -rotate-40 hidden lg:block"
        height={1067}
        imageClassName="w-full opacity-25 pointer-events-none select-none"
        mediaQuery={ORB_DESKTOP_MEDIA_QUERY}
        srcKey="landing/mind-ai-v2.png"
        width={1208}
      />

      <div className="relative mx-auto max-w-280 px-6 pt-6 pb-8 sm:py-12">
        <div className="flex justify-center">
          {/* Same `imageAlt` key as the b2c pages — it names the logo, not the colourway. */}
          <SVGImage
            alt={t('imageAlt:icons.houseOfMath')}
            height={32}
            imageClassName="w-42"
            loading="eager"
            srcKey="logos/house-of-math-white.svg"
            width={167}
          />
        </div>

        <h1 className="mt-4 text-lg font-bold text-center text-white sm:mt-12 sm:text-8.5">
          {t('classroom:roleSelection.title')}
        </h1>
        <p className="mt-2 text-xs text-center text-white sm:text-sm">{t('classroom:roleSelection.subtitle')}</p>

        <div className="mt-6 grid gap-4 sm:mt-8 sm:gap-8 md:grid-cols-2">
          <RoleCard testId="student-card" title={t('classroom:roleSelection.student')}>
            {/* "Get started as a student" is in the design but stays hidden this iteration. The route
                lives on — students reach /join/<code> from the teacher's invite — so the copy stays too. */}
            {/* Caps are the design's. */}
            <SchoolOutlineButton href={ROUTES.CLASSROOM_STUDENT_LOGIN} isFullWidth size="medium" testId="student-login">
              <span className="uppercase">{t('classroom:roleSelection.studentLogin')}</span>
            </SchoolOutlineButton>
          </RoleCard>

          <RoleCard testId="teacher-card" title={t('classroom:roleSelection.teacher')}>
            <ButtonPrimary href={ROUTES.CLASSROOM_TEACHER_LOGIN} isFullWidth size="medium" testId="teacher-login">
              {t('classroom:roleSelection.teacherLogin')}
            </ButtonPrimary>
            {/* Teachers are provisioned by an admin, so there is no self-service sign-up. */}
            <p className="text-13px text-center text-school-faint">{t('classroom:roleSelection.teacherHelp')}</p>
          </RoleCard>
        </div>

        {/* The partner mark, not decoration — it says whose platform this is, so it is named. */}
        <div className="mt-6 flex justify-center">
          <SVGImage
            alt={t('imageAlt:icons.csusa')}
            height={112}
            imageClassName="w-34 h-28"
            srcKey="partners/csusa.svg"
            width={136}
          />
        </div>
      </div>
    </main>
  )
}
